import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './Settings.css';

interface SubscriptionInfo {
  id: string;
  status: string;
  priceId: string;
  tier: string;
  amount: number;
  interval: string;
  currentPeriodEnd: number;
}

interface TierOption {
  tier: 'copper' | 'silver' | 'gold' | 'platinum' | 'platinum_annual';
  displayName: string;
  priceCents: number;
  interval: 'month' | 'year';
  action: 'current' | 'upgrade' | 'downgrade' | 'requires-cancel' | 'at-renewal' | 'unavailable';
  promoFirstMonthCents: number | null;
}

interface PlanOptions {
  currentTier: string | null;
  currentPlan: string | null;
  onAnnual: boolean;
  hasActiveSub: boolean;
  promoActiveForPlatinum: boolean;
  tiers: TierOption[];
}

interface OrderItem {
  id: string;
  orderNumber?: number;
  date: string;
  name: string;
  amount: number;
  type: 'service' | 'subscription' | 'purchase';
  status: string;
  receiptUrl: string | null;
}

interface UserProfile {
  id: number;
  email: string;
  username: string;
  role: string;
  created_at: string;
}

const getTierDisplayName = (tier: string): string => {
  const tierNames: { [key: string]: string } = {
    'platinum': 'Platinum',
    'gold': 'Gold',
    'silver': 'Silver',
    'copper': 'Copper'
  };
  return tierNames[tier.toLowerCase()] || 'No Plan';
};

const Settings: React.FC = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [planOptions, setPlanOptions] = useState<PlanOptions | null>(null);
  const [changingTier, setChangingTier] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [showAllOrders, setShowAllOrders] = useState(false);
  const [profileForm, setProfileForm] = useState({
    username: '',
    email: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (user) {
      setProfile(user as UserProfile);
      setProfileForm({
        username: user.username || '',
        email: user.email || ''
      });
      fetchSubscriptionInfo();
      fetchPlanOptions();
      fetchOrderHistory();
    }
  }, [user]);

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/user/subscription`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.data.subscription) {
        const sub = response.data.subscription;
        setSubscription({
          id: sub.id,
          status: sub.status,
          priceId: sub.priceId,
          tier: sub.tier || 'unknown',
          amount: sub.amount,
          interval: sub.interval,
          currentPeriodEnd: sub.currentPeriodEnd ? sub.currentPeriodEnd * 1000 : 0 // Convert to milliseconds
        });
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error('Failed to fetch subscription info:', error);
      setSubscription(null);
    }
  };

  const fetchOrderHistory = async () => {
    setOrdersLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/user/order-history`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.data.orders) {
        setOrders(response.data.orders);
      }
    } catch (error) {
      console.error('Failed to fetch order history:', error);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const getOrderTypeBadgeClass = (type: string): string => {
    switch (type) {
      case 'subscription': return 'order-type-subscription';
      case 'service': return 'order-type-service';
      case 'purchase': return 'order-type-purchase';
      default: return '';
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'paid':
      case 'completed':
      case 'finished':
        return 'order-status-completed';
      case 'backlog':
      case 'started':
        return 'order-status-pending';
      case 'blocked':
        return 'order-status-blocked';
      default:
        return '';
    }
  };

  const formatOrderStatus = (status: string): string => {
    switch (status) {
      case 'backlog': return 'Queued';
      case 'started': return 'In Progress';
      case 'blocked': return 'On Hold';
      case 'finished': return 'Delivered';
      case 'paid': return 'Paid';
      case 'completed': return 'Completed';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // In a real app, you'd have an update profile endpoint
      setMessage({ type: 'success', text: 'Profile update feature coming soon!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(
        `${API_BASE}/api/user/change-password`,
        {
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      setMessage({ type: 'success', text: response.data.message || 'Password changed successfully' });
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to change password';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/stripe/plan-options`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      setPlanOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch plan options:', error);
      setPlanOptions(null);
    }
  };

  const handleTierClick = async (target: TierOption) => {
    if (!planOptions) return;
    if (target.action === 'current' || target.action === 'unavailable') return;
    if (target.action === 'requires-cancel') {
      window.alert('To move to the free Copper tier, click "Manage Subscription" below and cancel your subscription. Your account stays open and you can still log in and play.');
      return;
    }
    if (target.action === 'at-renewal') {
      window.alert('Your annual plan can be changed when it renews. To stop it renewing, click "Manage Subscription" below; you keep Platinum until the end of the year you have paid for.');
      return;
    }

    const currentTier = planOptions.currentTier || 'current';
    let confirmMsg;
    if (target.tier === 'platinum_annual') {
      confirmMsg = `Switch to Platinum Annual?\n\nYou'll be charged $${(target.priceCents / 100).toFixed(2)} today for a year of Platinum, less a credit for the unused part of your current month. It renews yearly at $${(target.priceCents / 100).toFixed(2)} until you cancel. If you cancel, Platinum continues to the end of the paid year; the rest of that year is not refunded.`;
    } else if (target.tier === 'platinum' && target.promoFirstMonthCents !== null) {
      confirmMsg = `Upgrade to Platinum?\n\nYou'll be charged today for your first platinum month (50% off — about $${(target.promoFirstMonthCents / 100).toFixed(2)}, less any credit for unused ${currentTier} days). Renews at $${(target.priceCents / 100).toFixed(2)}/month.`;
    } else if (target.action === 'upgrade') {
      confirmMsg = `Upgrade to ${target.displayName}?\n\nYou'll get ${target.displayName} perks immediately. Your unused ${currentTier} time will be credited to your next invoice; the next bill is $${(target.priceCents / 100).toFixed(2)}/month after credit applies.`;
    } else {
      // downgrade
      confirmMsg = `Downgrade to ${target.displayName}?\n\nYou'll switch to ${target.displayName} perks immediately. Unused ${currentTier} time will be credited to your next invoice. Next billing cycle: $${(target.priceCents / 100).toFixed(2)}/month.`;
    }
    if (!window.confirm(confirmMsg)) return;

    setChangingTier(target.tier);
    setMessage({ type: '', text: '' });
    try {
      await axios.post(
        `${API_BASE}/api/stripe/change-plan`,
        { targetTier: target.tier },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );
      const verb = target.tier === 'platinum_annual' ? 'Switched'
        : target.action === 'upgrade' ? 'Upgraded' : 'Downgraded';
      setMessage({ type: 'success', text: `${verb} to ${target.displayName}!` });
      await fetchSubscriptionInfo();
      await fetchPlanOptions();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to change plan';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setChangingTier(null);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/stripe/create-portal-session`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      setMessage({ type: 'error', text: 'Failed to open subscription management portal' });
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="settings-page">
        <div className="container">
          <div className="access-denied">
            <h2>Access Denied</h2>
            <p>Please log in to access your settings.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="container">

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="settings-grid">
          {/* Profile Section */}
          <div className="settings-card">
            <h2>Profile Information</h2>
            <form onSubmit={handleProfileUpdate} className="settings-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                  disabled
                />
                <small>Email changes are not currently supported</small>
              </div>

              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={profileForm.username}
                  onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                  placeholder="Enter a username"
                />
              </div>

              <div className="account-info">
                <div className="info-item">
                  <span className="info-label">Account Type:</span>
                  <span className={`account-type ${user.role}`}>{user.role}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Member Since:</span>
                  <span>{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'Unknown'}</span>
                </div>
              </div>

              <button type="submit" className="update-btn" disabled={loading}>
                Update Profile
              </button>
            </form>
          </div>

          {/* Password Section */}
          <div className="settings-card">
            <h2>Change Password</h2>
            <form onSubmit={handlePasswordChange} className="settings-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    id="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                  >
                    {showCurrentPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    minLength={6}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                  >
                    {showNewPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    minLength={6}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <button type="submit" className="update-btn" disabled={loading}>
                Change Password
              </button>
            </form>
          </div>

          {/* Subscription Section */}
          <div className="settings-card">
            <h2>Subscription</h2>
            {subscription ? (
              <div className="subscription-info">
                <div className="subscription-status">
                  <span className={`status-badge ${subscription.status}`}>
                    {subscription.status.toUpperCase()}
                  </span>
                </div>
                
                <div className="subscription-details">
                  <div className="detail-item">
                    <span className="detail-label">Plan:</span>
                    <span>{getTierDisplayName(subscription.tier)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Amount:</span>
                    <span>${(subscription.amount / 100).toFixed(2)}/{subscription.interval}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Next Billing:</span>
                    <span>{subscription.currentPeriodEnd > 0 ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>

                {planOptions?.hasActiveSub && (
                  <div className="tier-changer" style={{ margin: '12px 0' }}>
                    <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>Change plan:</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {planOptions.tiers.map(t => {
                        const isCurrent = t.action === 'current';
                        const isBusy = changingTier === t.tier;
                        const hasPromo = t.promoFirstMonthCents !== null;
                        const priceLabel = hasPromo
                          ? `$${(t.promoFirstMonthCents! / 100).toFixed(2)}`
                          : t.priceCents === 0
                          ? 'Free'
                          : `$${(t.priceCents / 100).toFixed(2)}`;
                        const promoLabel = hasPromo ? ` (${t.displayName} sale)` : '';
                        return (
                          <button
                            key={t.tier}
                            onClick={() => handleTierClick(t)}
                            disabled={isCurrent || isBusy || changingTier !== null || loading}
                            style={{
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: isCurrent ? '2px solid #c9a227' : '1px solid #aaa',
                              background: isCurrent ? '#fffbe6' : '#fff',
                              cursor: isCurrent ? 'default' : 'pointer',
                              fontWeight: isCurrent ? 'bold' : 'normal',
                              minWidth: '110px',
                              textAlign: 'left'
                            }}
                            title={
                              t.action === 'requires-cancel'
                                ? 'Click for instructions on moving to the free Copper tier'
                                : t.action === 'at-renewal'
                                ? 'Annual plans change at renewal'
                                : t.action === 'upgrade'
                                ? `Upgrade to ${t.displayName}`
                                : t.action === 'downgrade'
                                ? `Downgrade to ${t.displayName}`
                                : 'Your current plan'
                            }
                          >
                            <div>{t.displayName}{isCurrent ? ' (current)' : ''}</div>
                            <div style={{ fontSize: '0.85em', color: '#555' }}>
                              {priceLabel}{t.priceCents > 0 && !hasPromo ? (t.interval === 'year' ? '/yr' : '/mo') : ''}{promoLabel}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {planOptions.onAnnual && (
                      <div style={{ fontSize: '0.85em', color: '#666', marginTop: '6px' }}>
                        You're on Platinum Annual. Your plan can be changed when it renews.
                      </div>
                    )}
                    {!planOptions.onAnnual && planOptions.tiers.some(t => t.tier === 'platinum_annual') && (
                      <div style={{ fontSize: '0.85em', color: '#666', marginTop: '6px' }}>
                        Platinum Annual: a year of Platinum for $300 -- two months free.
                      </div>
                    )}
                    {planOptions.promoActiveForPlatinum && planOptions.currentTier !== 'platinum' && (
                      <div style={{ fontSize: '0.85em', color: '#666', marginTop: '6px' }}>
                        Platinum upgrade includes 50% off your first month (May promo).
                      </div>
                    )}
                  </div>
                )}

                <div className="subscription-actions">
                  <button
                    className="manage-btn"
                    onClick={handleManageSubscription}
                    disabled={loading}
                  >
                    Manage Subscription
                  </button>
                </div>
              </div>
            ) : (
              <div className="no-subscription">
                <p>You don't have an active subscription.</p>
                <a href="/marketplace" className="subscribe-link">
                  Browse subscription plans →
                </a>
              </div>
            )}
          </div>

          {/* Order History Section */}
          <div className="settings-card">
            <h2>Order History</h2>
            {ordersLoading ? (
              <div className="orders-loading">Loading orders...</div>
            ) : orders.length > 0 ? (
              <div className="order-history">
                <div className={`order-list ${showAllOrders ? 'order-list-expanded' : ''}`}>
                  {(showAllOrders ? orders : orders.slice(0, 10)).map((order) => (
                    <div key={order.id} className="order-item">
                      <div className="order-main">
                        <div className="order-name">
                          {order.name}
                          {order.orderNumber && (
                            <span className="order-id-badge">Order #{order.orderNumber}</span>
                          )}
                        </div>
                        <div className="order-meta">
                          <span className={`order-type-badge ${getOrderTypeBadgeClass(order.type)}`}>
                            {order.type.charAt(0).toUpperCase() + order.type.slice(1)}
                          </span>
                          <span className={`order-status-badge ${getStatusBadgeClass(order.status)}`}>
                            {formatOrderStatus(order.status)}
                          </span>
                        </div>
                      </div>
                      <div className="order-details">
                        <span className="order-amount">${(order.amount / 100).toFixed(2)}</span>
                        <span className="order-date">
                          {new Date(order.date).toLocaleDateString()}
                        </span>
                        {order.receiptUrl && (
                          <a
                            href={order.receiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="receipt-link"
                          >
                            Receipt
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {orders.length > 10 && (
                  <button
                    className="view-all-orders-btn"
                    onClick={() => setShowAllOrders(!showAllOrders)}
                  >
                    {showAllOrders
                      ? 'Show Recent Orders'
                      : `View All Orders (${orders.length})`}
                  </button>
                )}
              </div>
            ) : (
              <div className="no-orders">
                <p>No orders yet.</p>
                <a href="/marketplace" className="subscribe-link">
                  Browse the marketplace →
                </a>
              </div>
            )}
          </div>

          {/* Account Actions */}
          <div className="settings-card danger-zone">
            <h2>Account Actions</h2>
            <div className="danger-actions">
              <button 
                className="logout-btn"
                onClick={logout}
              >
                Logout
              </button>
              <button 
                className="delete-btn"
                onClick={() => alert('Account deletion feature coming soon!')}
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;